<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Clientes</h1>
      <!--<v-btn color="primary" @click="handleCreateClient">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Cliente
      </v-btn>-->
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por nome ou e-mail" clearable
              hide-details prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="selectedIsActive" :items="activeOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="clients" :items-length="totalItems" :loading="isLoading"
          :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage" class="elevation-0"
          @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <!--<v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir" />-->
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados dos clientes.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza de que deseja excluir o cliente
          <strong>{{ itemToDelete.name }}</strong> (ID: <strong>{{ itemToDelete.id }}</strong>)?
          Esta ação é irreversível.
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore'
import dayjs from 'dayjs'

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()

const clients = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedIsActive = ref(null)
const selectedPlanStatus = ref(null)

const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

const deleteDialog = ref(false)
const itemToDelete = ref({})

const activeOptions = [
  { title: 'Ativo', value: '1' },
  { title: 'Inativo', value: '0' }
]

const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  return 'Nenhum cliente encontrado.'
})

const columns = [
  { title: 'ID', key: 'id', sortable: true },
  { title: 'Nome', key: 'name', sortable: true },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'WhatsApp', key: 'phone', sortable: true },
  { title: 'Status', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]

const loadClients = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show()
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      search: searchTerm.value,
      is_active: selectedIsActive.value,
    }

    const response = await api.get('/clients', { params })

    clients.value = response.data.data
    totalItems.value = response.data.total
    itemsPerPage.value = response.data.per_page
    page.value = response.data.current_page

  } catch (error) {
    console.error('Erro ao carregar clientes:', error)
    hasError.value = true
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar a lista de clientes.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingStore.hide()
  }
}

watch([page, itemsPerPage], () => {
  loadClients()
})

const onFiltersChange = () => {
  page.value = 1
  loadClients()
}

const handleCreateClient = () => {
  router.push('/app/clientes/novo') 
}

const handleEdit = async (row) => {
  router.push({ name: 'client-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/clients/${itemToDelete.value.id}`) // Adaptar o endpoint
    toastStore.showToast({
      message: 'Cliente excluído com sucesso!',
      color: 'success'
    })

    // Recarrega a lista
    await loadClients()
  } catch (error) {
    console.error('Erro ao excluir cliente:', error)
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao excluir cliente. Verifique se existem dependências.',
      color: 'error'
    })
  }
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativo' : 'Inativo'
}

function formatDateTime(dateTime) {
    if (!dateTime) return 'N/A';
    return dayjs(dateTime).format('DD/MM/YYYY');
}

onMounted(() => {
  loadClients()
})
</script>

<style scoped>
</style>